<template>
  <v-app-bar flat class="tempus-nav" :height="64">
    <!-- Logo / Wordmark -->
    <router-link to="/" class="tempus-wordmark text-decoration-none d-flex align-center ml-4">
      <span class="wordmark-text">Tempus</span>
    </router-link>

    <!-- Desktop Navigation -->
    <div class="nav-links d-none d-md-flex align-center ml-8">
      <router-link
        v-for="item in navItems"
        :key="item.to"
        :to="item.to"
        class="nav-link text-decoration-none"
        :class="{ 'nav-link--active': isActive(item.to) }"
      >
        <v-icon size="18" class="mr-1">{{ item.icon }}</v-icon>
        {{ item.title }}
      </router-link>
    </div>

    <v-spacer />

    <!-- New / Continue Session CTA -->
    <v-btn
      color="primary"
      variant="flat"
      to="/sessions/new"
      class="mr-2 d-none d-sm-flex"
      prepend-icon="mdi-play-circle"
      size="small"
    >
      {{ hasActiveSession ? 'Continue Session' : 'New Session' }}
    </v-btn>

    <!-- User menu (desktop) -->
    <v-menu location="bottom end">
      <template #activator="{ props }">
        <v-btn icon variant="text" class="ml-1 d-none d-md-flex" v-bind="props">
          <v-icon>mdi-account-circle</v-icon>
        </v-btn>
      </template>
      <v-list density="compact" min-width="200">
        <v-list-item v-if="userEmail" :subtitle="userEmail" title="Signed in" />
        <v-divider />
        <v-list-item prepend-icon="mdi-logout" title="Sign out" @click="handleLogout" />
      </v-list>
    </v-menu>

    <!-- Mobile Menu Toggle -->
    <v-btn icon variant="text" class="d-md-none" @click="mobileMenuOpen = true">
      <v-icon>mdi-menu</v-icon>
    </v-btn>
  </v-app-bar>

  <!-- Mobile Navigation Drawer -->
  <v-navigation-drawer
    v-model="mobileMenuOpen"
    location="right"
    temporary
    width="280"
    class="tempus-mobile-drawer"
  >
    <div class="pa-4">
      <div class="d-flex align-center justify-space-between mb-4">
        <span class="font-display text-h6">Tempus</span>
        <v-btn icon variant="text" size="small" @click="mobileMenuOpen = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-btn
        color="primary"
        variant="flat"
        to="/sessions/new"
        block
        class="mb-4"
        prepend-icon="mdi-play-circle"
        @click="mobileMenuOpen = false"
      >
        {{ hasActiveSession ? 'Continue Session' : 'New Session' }}
      </v-btn>

      <v-divider class="mb-2" />

      <v-list density="compact" nav>
        <v-list-item
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          :prepend-icon="item.icon"
          @click="mobileMenuOpen = false"
        >
          {{ item.title }}
        </v-list-item>
      </v-list>

      <v-divider class="my-2" />

      <v-list density="compact" nav>
        <v-list-item prepend-icon="mdi-logout" title="Sign out" @click="handleLogout" />
      </v-list>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useActiveSessionQuery } from '@/composables/useSessions'
import { useAppStore } from '@/stores/app'
import { useAuth } from '@/composables/useAuth'

interface NavItem {
  title: string
  icon: string
  to: string
}

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const { logout } = useAuth()
const mobileMenuOpen = ref(false)

const userEmail = computed((): string => appStore.currentUser?.email ?? '')

async function handleLogout(): Promise<void> {
  mobileMenuOpen.value = false
  await logout()
  await router.push({ name: 'login' })
}

// Reflect whether a practice session is currently in progress so the CTA can
// invite the user to continue it rather than start a new one.
const { data: activeSession } = useActiveSessionQuery()
const hasActiveSession = computed((): boolean => !!activeSession.value)

const navItems: NavItem[] = [
  { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/' },
  { title: 'Sessions', icon: 'mdi-calendar-clock', to: '/sessions' },
  { title: 'Exercises', icon: 'mdi-music-note', to: '/exercises' },
  { title: 'Review', icon: 'mdi-cards-outline', to: '/review' },
  { title: 'Categories', icon: 'mdi-folder', to: '/categories' },
  { title: 'Tags', icon: 'mdi-tag-multiple', to: '/tags' },
  { title: 'New Breed', icon: 'mdi-book-open-page-variant', to: '/new-breed' },
  { title: 'Settings', icon: 'mdi-cog', to: '/settings' },
]

function isActive(to: string): boolean {
  if (to === '/') return route.path === '/'
  return route.path.startsWith(to)
}
</script>

<style scoped>
.tempus-nav {
  background: rgba(26, 23, 20, 0.85) !important;
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #332e28 !important;
}

.wordmark-text {
  font-family: 'DM Serif Display', Georgia, serif;
  font-size: 1.4rem;
  color: #e8e0d8;
  letter-spacing: 0.02em;
}

.nav-links {
  gap: 4px;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #b5aa9e;
  transition: color 0.2s, background-color 0.2s;
}

.nav-link:hover {
  color: #e8e0d8;
  background: rgba(51, 46, 40, 0.6);
}

.nav-link--active {
  color: #d4a574 !important;
  background: rgba(212, 165, 116, 0.1);
}

.nav-link--active:hover {
  background: rgba(212, 165, 116, 0.15);
}

.tempus-mobile-drawer {
  background: #2a2520 !important;
}
</style>
